import React, { useState, useEffect, useRef } from 'react';
import BeaconProChart from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import { ContentSpiderChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentSpiderChart';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import LegendIndicator from 'src/components/BeaconV2Components/Legend/LegendIndicator';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useComparisonPeriod, useMetricFormatter } from 'src/utils/Hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { ChartDataAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import { KeyMetricsData } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/KeyMetricsAdapter';
import { SpiderChartLoadingSkeleton } from 'src/components/BeaconRedesignComponents/SpiderChart/SpiderChartLoadingSkeleton';
import { SpiderChartProps } from 'src/components/BeaconRedesignComponents/SpiderChart/SpiderChart';
import { ContentMetricType } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';

type ActiveView = 'both' | 'prior' | 'current';

interface ContentKeyMetricsSpiderChartProps {
  title: string;
  queryFn: () => Promise<any>;
  /**
   * Key used to determine subtitle content and queryKey uniqueness
   */
  type: ContentMetricType;
  spiderChartProps?: Partial<SpiderChartProps>;
  renderSubtitle: (formattedMetric: string) => React.ReactNode;
}

const metricTypeToCount = {
  [ContentMetricType.ContentScore]: 7,
  [ContentMetricType.ContentUtilizationScore]: 6,
  [ContentMetricType.ContentAccuracyScore]: 5
};

const ContentKeyMetricsSpiderChart = ({
  title,
  queryFn,
  type,
  renderSubtitle,
  spiderChartProps = {}
}: ContentKeyMetricsSpiderChartProps) => {
  const formatMetric = useMetricFormatter();
  const chartRef = useRef<HTMLDivElement>(null);
  const [activeView, setActiveView] = useState<ActiveView>('both');
  const pId = useComparisonPeriod();

  const handleViewToggle = (view: ActiveView) => {
    setActiveView(view === activeView ? 'both' : view);
  };

  // This effect ensures that the label click handlers are reattached after the view changes
  useEffect(() => {
    // Give the chart time to re-render after view change
    const timeoutId = setTimeout(() => {
      if (chartRef.current && spiderChartProps.handleLabelClick) {
        // Find all clickable labels in the chart
        const clickableLabels = chartRef.current.querySelectorAll('.clickableLabel');

        // Re-attach event listeners
        clickableLabels.forEach((label) => {
          if (label instanceof HTMLElement) {
            // We need to find the category data for this label
            const labelText = label.querySelector('span')?.textContent;
            if (labelText) {
              label.addEventListener('click', () => {
                if (spiderChartProps.handleLabelClick) {
                  spiderChartProps.handleLabelClick({
                    chart: null,
                    labelData: {
                      name: labelText,
                      value: 0,
                      change: 0
                    }
                  });
                }
              });
            }
          }
        });
      }
    }, 300); // Small delay to ensure chart has re-rendered

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, spiderChartProps.handleLabelClick]);

  return (
    <BeaconProChart queryFn={queryFn} name={`content-spider-chart-${title.toLowerCase().replace(/\s+/g, '-')}-${type}`}>
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(data: ChartDataAdapter<KeyMetricsData>, isLoading) => {
          if (isLoading) {
            return 'Loading subtitle...';
          }

          const { primaryOverallScore, secondaryOverallScore } = data.transform();

          let scoreToDisplay = primaryOverallScore;
          if (activeView === 'prior' && secondaryOverallScore !== undefined) {
            scoreToDisplay = secondaryOverallScore;
          }

          const formattedMetric = formatMetric(scoreToDisplay, METRICTYPE.PERCENT, {
            showFullValue: true,
            decimalPlaces: 1
          });

          return renderSubtitle(formattedMetric);
        }}
      />

      <BeaconProChart.Legend
        renderLoading={() => null}
        render={(data: ChartDataAdapter<KeyMetricsData>) => {
          const { primaryOverallScore, secondaryData } = data.transform();
          const legendColor =
            primaryOverallScore >= 0.7 ? 'success' : primaryOverallScore < 0.5 ? 'error' : 'accentGold';

          return (
            <Flex sx={{ marginTop: '32px' }} gap={32}>
              {secondaryData && (
                <LegendIndicator
                  displayName={pId === 'prior-year' ? 'Prior Year' : 'Prior Period'}
                  color="casper"
                  legendOnClick={() => handleViewToggle('prior')}
                  opacity={activeView === 'current' ? 0.5 : 1}
                />
              )}
              <LegendIndicator
                displayName="Current Period"
                color={legendColor}
                legendOnClick={() => handleViewToggle('current')}
                opacity={activeView === 'prior' ? 0.5 : 1}
              />
            </Flex>
          );
        }}
      />
      <BeaconProChart.Visualization
        renderLoading={() => (
          <div
            style={{
              width: '448px',
              height: '448px',
              margin: '0 auto',
              marginTop: '132px'
            }}
          >
            <SpiderChartLoadingSkeleton numPoints={metricTypeToCount[type]} />
          </div>
        )}
        render={(data: ChartDataAdapter<KeyMetricsData>) => {
          const { primaryOverallScore, primaryData, secondaryData, secondaryOverallScore } = data.transform();

          let primaryDataToDisplay = primaryData;
          let secondaryDataToDisplay;
          let gaugeValue = primaryOverallScore;

          let gaugeColor;

          if (activeView === 'prior' && secondaryData) {
            primaryDataToDisplay = secondaryData.map((item) => ({
              ...item,
              change: 0
            }));
            gaugeValue = secondaryOverallScore || 0;

            secondaryDataToDisplay = [];

            gaugeColor = '#ADBDCC';
          } else if (activeView === 'both') {
            secondaryDataToDisplay = secondaryData;
          } else if (activeView === 'current') {
            secondaryDataToDisplay = [];
          }

          const modifiedSpiderChartProps = {
            ...spiderChartProps,
            animation: false
          };

          if (activeView !== 'both') {
            modifiedSpiderChartProps.comparisonData = [];
          }

          if (activeView === 'prior') {
            modifiedSpiderChartProps.getChartOptions = (defaultOptions) => {
              const baseOptions = spiderChartProps.getChartOptions
                ? spiderChartProps.getChartOptions(defaultOptions)
                : defaultOptions;

              const newSeries = [...(baseOptions.series || [])];

              if (newSeries.length > 1) {
                const primarySeries = newSeries[1] as any;
                if (primarySeries) {
                  primarySeries.color = '#ADBDCC';
                  primarySeries.fillColor = 'rgba(173, 189, 204, 0.2)';

                  if (primarySeries.marker) {
                    primarySeries.marker.lineColor = '#D1D6DD';
                    primarySeries.marker.fillColor = '#ADBDCC';
                  }
                }
              }

              return {
                ...baseOptions,
                series: newSeries,
                chart: {
                  ...(baseOptions.chart || {}),
                  animation: false,
                  events: {
                    ...(baseOptions.chart?.events || {}),

                    render() {
                      // Force consistent positioning of labels after render
                      const xAxis = this.xAxis[0];
                      if (xAxis && xAxis.labelGroup) {
                        xAxis.labelGroup.attr({ translateX: 0, translateY: 0 });
                      }

                      if (spiderChartProps.handleLabelClick) {
                        const { container } = this;
                        if (container) {
                          const spans = container.querySelectorAll('.clickableLabel');
                          spans.forEach((span, index) => {
                            // eslint-disable-next-line react/no-this-in-sfc
                            const category = this.xAxis[0].categories[index];
                            if (span instanceof HTMLElement) {
                              span.addEventListener('click', () => {
                                if (spiderChartProps.handleLabelClick) {
                                  spiderChartProps.handleLabelClick({
                                    chart: this,
                                    labelData: category
                                  });
                                }
                              });
                            }
                          });
                        }
                      }
                    }
                  }
                },
                plotOptions: {
                  ...(baseOptions.plotOptions || {}),
                  series: {
                    ...(baseOptions.plotOptions?.series || {}),
                    animation: false
                  }
                }
              };
            };
          }

          return (
            <div ref={chartRef} style={{ width: '448px', height: '448px', margin: '0 auto', marginTop: '132px' }}>
              <ContentSpiderChart
                primarySpiderData={primaryDataToDisplay.map((datum) => datum.value)}
                secondarySpiderData={secondaryDataToDisplay?.map((datum) => datum.value)}
                gaugeValue={gaugeValue}
                gaugeText={formatMetric(gaugeValue, METRICTYPE.PERCENT, {
                  showFullValue: true,
                  decimalPlaces: 1
                })}
                categories={primaryDataToDisplay}
                gaugeColor={gaugeColor}
                spiderChartProps={{
                  chartWidth: 448,
                  chartHeight: 448,
                  animation: false,
                  ...modifiedSpiderChartProps
                }}
              />
            </div>
          );
        }}
      />
    </BeaconProChart>
  );
};

export default ContentKeyMetricsSpiderChart;
